import {
  CreateLinkBody,
  LinkDTO,
  ListLinksQuery,
  ListLinksResponse,
  MeResponse,
  UpdateLinkBody,
} from "@shortner/shared";
import { ApiError, type AppErrorCode } from "@shared/lib/api-error";

interface SchemaLike<T> {
  safeParse(
    input: unknown,
  ): { success: true; data: T } | { success: false; error: { message: string } };
}

interface ApiClientConfig {
  baseUrl: string;
  getToken: () => string | null;
  fetch?: typeof globalThis.fetch;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string } } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError({
    code: (body.error?.code as AppErrorCode | undefined) ?? "INTERNAL",
    message: body.error?.message ?? `Request failed with status ${res.status}`,
    status: res.status,
  });
}

async function parseAs<T>(res: Response, schema: SchemaLike<T>): Promise<T> {
  const json = (await res.json()) as unknown;
  const result = schema.safeParse(json);
  if (!result.success) {
    throw new Error(`Response schema mismatch: ${result.error.message}`);
  }
  return result.data;
}

export function createApiClient(config: ApiClientConfig) {
  const baseUrl = config.baseUrl.replace(/\/$/, "");
  const f = config.fetch ?? globalThis.fetch;

  async function call(path: string, init?: RequestInit): Promise<Response> {
    const token = config.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(init?.headers as Record<string, string> | undefined),
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await f(`${baseUrl}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    return res;
  }

  return {
    async health(): Promise<{ status: string }> {
      const res = await call("/health");
      return (await res.json()) as { status: string };
    },

    async getMe(): Promise<MeResponse> {
      const res = await call("/api/me");
      return parseAs(res, MeResponse);
    },

    async listLinks(query: ListLinksQuery): Promise<ListLinksResponse> {
      const params = new URLSearchParams({
        limit: String(query.limit),
        offset: String(query.offset),
      });
      const res = await call(`/api/links?${params.toString()}`);
      return parseAs(res, ListLinksResponse);
    },

    async createLink(body: CreateLinkBody): Promise<LinkDTO> {
      const res = await call("/api/links", { method: "POST", body: JSON.stringify(body) });
      return parseAs(res, LinkDTO);
    },

    async updateLink(id: string, body: UpdateLinkBody): Promise<LinkDTO> {
      const res = await call(`/api/links/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      return parseAs(res, LinkDTO);
    },

    async deleteLink(id: string): Promise<void> {
      await call(`/api/links/${id}`, { method: "DELETE" });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
